import { getDb } from "@/services/storage/sqlite/connection";
import { isFtsAvailable } from "@/services/storage/sqlite/schema";
import { SNIPPET_MARK_CLOSE, SNIPPET_MARK_OPEN } from "@/utils/highlight";
import { logger } from "@/services/logger";
import type { UnifiedSearchResult } from "@/types";

const log = logger.scope("search");

/**
 * Full-text search over SQLite. Prefers FTS5 (fast, ranked, with snippets);
 * transparently falls back to `LIKE` if the host's bundled SQLite wasn't
 * compiled with FTS5 (see `sqlite/schema.ts`'s `isFtsAvailable`) — slower and
 * title/prefix-only, but never a hard failure.
 *
 * Snippets are delimited with plain sentinel characters, not `<mark>` tags —
 * FTS5's `snippet()` doesn't escape the surrounding text, and that text
 * comes from the user's own files, so raw HTML tags here would be an
 * injection risk. `utils/highlight.ts` escapes the content and substitutes
 * real markup afterward, once it's safe to.
 */
const SNIPPET_TAG_OPEN = SNIPPET_MARK_OPEN;
const SNIPPET_TAG_CLOSE = SNIPPET_MARK_CLOSE;
const SNIPPET_ELLIPSIS = "…";

export interface ConversationSearchHit {
  id: string;
  title: string;
  snippet: string;
  updatedAt: number;
}

export interface KnowledgeSearchHit {
  documentId: string;
  documentName: string;
  chunkId: string;
  snippet: string;
}

/** Quotes each token so punctuation/FTS operators in raw user input can't
 * break MATCH syntax, and applies prefix matching so results appear as the
 * user is still typing the last word. */
export function toMatchQuery(query: string): string {
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  return tokens.map((t) => `"${t.replace(/"/g, '""')}"*`).join(" ");
}

export async function searchConversations(query: string, limit = 20): Promise<ConversationSearchHit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<
      { id: string; title: string; updated_at: number; snippet: string }[]
    >(
      `SELECT c.id, c.title, c.updated_at,
              snippet(conversations_fts, 0, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 8) AS snippet
       FROM conversations_fts
       JOIN conversations c ON c.rowid = conversations_fts.rowid
       WHERE conversations_fts MATCH $1
       ORDER BY rank
       LIMIT $2`,
      [toMatchQuery(trimmed), limit],
    );
    return rows.map((r) => ({ id: r.id, title: r.title, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; title: string; updated_at: number }[]>(
    "SELECT id, title, updated_at FROM conversations WHERE title LIKE $1 ORDER BY updated_at DESC LIMIT $2",
    [`%${trimmed}%`, limit],
  );
  return rows.map((r) => ({ id: r.id, title: r.title, snippet: r.title, updatedAt: r.updated_at }));
}

/** Searches message content specifically (as distinct from titles) — used
 * to surface conversations by what was said in them, not just their name. */
export async function searchConversationMessages(
  query: string,
  limit = 20,
): Promise<ConversationSearchHit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<
      { id: string; title: string; updated_at: number; snippet: string }[]
    >(
      `SELECT c.id, c.title, c.updated_at,
              snippet(messages_fts, 0, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 10) AS snippet
       FROM messages_fts
       JOIN messages m ON m.rowid = messages_fts.rowid
       JOIN conversations c ON c.id = m.conversation_id
       WHERE messages_fts MATCH $1
       GROUP BY c.id
       ORDER BY rank
       LIMIT $2`,
      [toMatchQuery(trimmed), limit],
    );
    return rows.map((r) => ({ id: r.id, title: r.title, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; title: string; updated_at: number; content: string }[]>(
    `SELECT DISTINCT c.id, c.title, c.updated_at, m.content
     FROM messages m JOIN conversations c ON c.id = m.conversation_id
     WHERE m.content LIKE $1
     ORDER BY c.updated_at DESC
     LIMIT $2`,
    [`%${trimmed}%`, limit],
  );
  return rows.map((r) => ({ id: r.id, title: r.title, snippet: r.content.slice(0, 160), updatedAt: r.updated_at }));
}

export async function searchKnowledge(query: string, limit = 20): Promise<KnowledgeSearchHit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<
      { chunk_id: string; document_id: string; name: string; snippet: string }[]
    >(
      `SELECT ch.id AS chunk_id, ch.document_id, d.name,
              snippet(chunks_fts, 0, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 12) AS snippet
       FROM chunks_fts
       JOIN chunks ch ON ch.rowid = chunks_fts.rowid
       JOIN documents d ON d.id = ch.document_id
       WHERE chunks_fts MATCH $1
       ORDER BY rank
       LIMIT $2`,
      [toMatchQuery(trimmed), limit],
    );
    return rows.map((r) => ({
      documentId: r.document_id,
      documentName: r.name,
      chunkId: r.chunk_id,
      snippet: r.snippet,
    }));
  }

  const rows = await db.select<{ id: string; document_id: string; content: string; name: string }[]>(
    `SELECT ch.id, ch.document_id, ch.content, d.name
     FROM chunks ch JOIN documents d ON d.id = ch.document_id
     WHERE ch.content LIKE $1
     LIMIT $2`,
    [`%${trimmed}%`, limit],
  );
  return rows.map((r) => ({
    documentId: r.document_id,
    documentName: r.name,
    chunkId: r.id,
    snippet: r.content.slice(0, 200),
  }));
}

// --- Phase 4: notes/prompts/projects/tasks + unified federation ------------

export async function searchNotes(query: string, workspaceId: string, limit = 20): Promise<UnifiedSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<{ id: string; title: string; updated_at: number; snippet: string }[]>(
      `SELECT n.id, n.title, n.updated_at,
              snippet(notes_fts, 1, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 10) AS snippet
       FROM notes_fts JOIN notes n ON n.rowid = notes_fts.rowid
       WHERE notes_fts MATCH $1 AND n.workspace_id = $2
       ORDER BY rank LIMIT $3`,
      [toMatchQuery(trimmed), workspaceId, limit],
    );
    return rows.map((r) => ({ kind: "note", id: r.id, title: r.title, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; title: string; updated_at: number; content: string }[]>(
    `SELECT id, title, updated_at, content FROM notes WHERE workspace_id = $2 AND (title LIKE $1 OR content LIKE $1)
     ORDER BY updated_at DESC LIMIT $3`,
    [`%${trimmed}%`, workspaceId, limit],
  );
  return rows.map((r) => ({
    kind: "note",
    id: r.id,
    title: r.title,
    snippet: r.content.slice(0, 160),
    updatedAt: r.updated_at,
  }));
}

export async function searchPrompts(query: string, workspaceId: string, limit = 20): Promise<UnifiedSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<{ id: string; title: string; updated_at: number; snippet: string }[]>(
      `SELECT p.id, p.title, p.updated_at,
              snippet(prompts_fts, 1, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 10) AS snippet
       FROM prompts_fts JOIN prompts p ON p.rowid = prompts_fts.rowid
       WHERE prompts_fts MATCH $1 AND p.workspace_id = $2
       ORDER BY rank LIMIT $3`,
      [toMatchQuery(trimmed), workspaceId, limit],
    );
    return rows.map((r) => ({ kind: "prompt", id: r.id, title: r.title, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; title: string; updated_at: number; body: string }[]>(
    `SELECT id, title, updated_at, body FROM prompts WHERE workspace_id = $2 AND (title LIKE $1 OR body LIKE $1)
     ORDER BY updated_at DESC LIMIT $3`,
    [`%${trimmed}%`, workspaceId, limit],
  );
  return rows.map((r) => ({
    kind: "prompt",
    id: r.id,
    title: r.title,
    snippet: r.body.slice(0, 160),
    updatedAt: r.updated_at,
  }));
}

export async function searchProjects(query: string, workspaceId: string, limit = 20): Promise<UnifiedSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<{ id: string; name: string; updated_at: number; snippet: string }[]>(
      `SELECT pr.id, pr.name, pr.updated_at,
              snippet(projects_fts, 1, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 10) AS snippet
       FROM projects_fts JOIN projects pr ON pr.rowid = projects_fts.rowid
       WHERE projects_fts MATCH $1 AND pr.workspace_id = $2
       ORDER BY rank LIMIT $3`,
      [toMatchQuery(trimmed), workspaceId, limit],
    );
    return rows.map((r) => ({ kind: "project", id: r.id, title: r.name, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; name: string; updated_at: number; description: string }[]>(
    `SELECT id, name, updated_at, description FROM projects WHERE workspace_id = $2 AND (name LIKE $1 OR description LIKE $1)
     ORDER BY updated_at DESC LIMIT $3`,
    [`%${trimmed}%`, workspaceId, limit],
  );
  return rows.map((r) => ({
    kind: "project",
    id: r.id,
    title: r.name,
    snippet: r.description.slice(0, 160),
    updatedAt: r.updated_at,
  }));
}

export async function searchTasks(query: string, workspaceId: string, limit = 20): Promise<UnifiedSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const db = await getDb();

  if (isFtsAvailable()) {
    const rows = await db.select<{ id: string; title: string; updated_at: number; snippet: string }[]>(
      `SELECT t.id, t.title, t.updated_at,
              snippet(tasks_fts, 1, '${SNIPPET_TAG_OPEN}', '${SNIPPET_TAG_CLOSE}', '${SNIPPET_ELLIPSIS}', 10) AS snippet
       FROM tasks_fts JOIN tasks t ON t.rowid = tasks_fts.rowid
       WHERE tasks_fts MATCH $1 AND t.workspace_id = $2
       ORDER BY rank LIMIT $3`,
      [toMatchQuery(trimmed), workspaceId, limit],
    );
    return rows.map((r) => ({ kind: "task", id: r.id, title: r.title, snippet: r.snippet, updatedAt: r.updated_at }));
  }

  const rows = await db.select<{ id: string; title: string; updated_at: number; notes: string }[]>(
    `SELECT id, title, updated_at, notes FROM tasks WHERE workspace_id = $2 AND (title LIKE $1 OR notes LIKE $1)
     ORDER BY updated_at DESC LIMIT $3`,
    [`%${trimmed}%`, workspaceId, limit],
  );
  return rows.map((r) => ({
    kind: "task",
    id: r.id,
    title: r.title,
    snippet: r.notes.slice(0, 160),
    updatedAt: r.updated_at,
  }));
}

/** Federates every per-entity search into one ranked list — consumed by
 * `CommandPalette` and `SearchPage`. Conversations/knowledge stay global
 * (matching their existing tag-not-query workspace treatment, see
 * `WORKSPACE_SYSTEM.md`); notes/prompts/projects/tasks are scoped to
 * `workspaceId`. Each entity gets its own small `limit` so the merged list
 * stays bounded; results are then sorted by recency rather than attempting
 * to unify BM25 ranks across separate FTS5 tables. */
export async function searchUnified(
  query: string,
  workspaceId: string,
  perEntityLimit = 6,
): Promise<UnifiedSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const [conversations, knowledge, notes, prompts, projects, tasks] = await Promise.all([
    searchConversations(trimmed, perEntityLimit).catch((err: unknown) => {
      log.warn("Conversation search failed", err);
      return [] as ConversationSearchHit[];
    }),
    searchKnowledge(trimmed, perEntityLimit).catch((err: unknown) => {
      log.warn("Knowledge search failed", err);
      return [] as KnowledgeSearchHit[];
    }),
    searchNotes(trimmed, workspaceId, perEntityLimit).catch((err: unknown) => {
      log.warn("Note search failed", err);
      return [] as UnifiedSearchResult[];
    }),
    searchPrompts(trimmed, workspaceId, perEntityLimit).catch((err: unknown) => {
      log.warn("Prompt search failed", err);
      return [] as UnifiedSearchResult[];
    }),
    searchProjects(trimmed, workspaceId, perEntityLimit).catch((err: unknown) => {
      log.warn("Project search failed", err);
      return [] as UnifiedSearchResult[];
    }),
    searchTasks(trimmed, workspaceId, perEntityLimit).catch((err: unknown) => {
      log.warn("Task search failed", err);
      return [] as UnifiedSearchResult[];
    }),
  ]);

  const conversationResults: UnifiedSearchResult[] = conversations.map((c) => ({
    kind: "conversation",
    id: c.id,
    title: c.title,
    snippet: c.snippet,
    updatedAt: c.updatedAt,
  }));
  const knowledgeResults: UnifiedSearchResult[] = knowledge.map((k) => ({
    kind: "knowledge",
    id: k.chunkId,
    title: k.documentName,
    snippet: k.snippet,
    updatedAt: 0,
  }));

  return [...conversationResults, ...knowledgeResults, ...notes, ...prompts, ...projects, ...tasks].sort(
    (a, b) => b.updatedAt - a.updatedAt,
  );
}
